/**
 * SectionIllustrations.jsx
 *
 * Five compact topic-based illustrations used across the industry pages.
 * Same flat-fintech language used elsewhere on the Swipetronics site:
 * soft-blue panel backdrop, dotted texture, radial halo, floating cards,
 * navy / accent-blue / soft-blue / white palette only.
 *
 * Each renders at a landscape 460x260 viewBox so it fits the right-column
 * of a section header on desktop without dominating the layout.
 */

function DefsBlock({ id }) {
  return (
    <defs>
      <linearGradient id={`${id}-panel`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#EAF3FF" /><stop offset="100%" stopColor="#F4F8FF" />
      </linearGradient>
      <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#0072FF" /><stop offset="100%" stopColor="#3A8EFF" />
      </linearGradient>
      <linearGradient id={`${id}-navy`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#0A2552" /><stop offset="100%" stopColor="#061A3A" />
      </linearGradient>
      <radialGradient id={`${id}-halo`} cx="50%" cy="50%" r="55%">
        <stop offset="0%" stopColor="#0072FF" stopOpacity="0.25" />
        <stop offset="70%" stopColor="#0072FF" stopOpacity="0.05" />
        <stop offset="100%" stopColor="#0072FF" stopOpacity="0" />
      </radialGradient>
      <pattern id={`${id}-dots`} x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse">
        <circle cx="1.4" cy="1.4" r="1.4" fill="#0072FF" fillOpacity="0.16" />
      </pattern>
      <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
        <feOffset dy="10" />
        <feComponentTransfer><feFuncA type="linear" slope="0.22" /></feComponentTransfer>
        <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>
  );
}

function Frame({ id, children, label }) {
  return (
    <svg viewBox="0 0 460 260" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto max-w-[460px] mx-auto" role="img" aria-label={label}>
      <DefsBlock id={id} />
      <rect x="8" y="8" width="444" height="244" rx="22" fill={`url(#${id}-panel)`} />
      <rect x="8" y="8" width="444" height="244" rx="22" fill={`url(#${id}-dots)`} opacity="0.55" />
      <ellipse cx="230" cy="130" rx="200" ry="130" fill={`url(#${id}-halo)`} />
      {children}
    </svg>
  );
}

/* ── 1) CHALLENGES — statement (neutral, no fake $ or "avoidable" flags) ── */
export function ChallengesIllustration() {
  const id = "sc";
  return (
    <Frame id={id} label="Merchant processing statement laid out for review">
      <rect x="40" y="46" width="34" height="34" rx="10" fill="#0072FF" fillOpacity="0.14" transform="rotate(-12 57 63)" />
      <circle cx="408" cy="60" r="20" fill="#3A8EFF" fillOpacity="0.18" />
      <g transform="translate(70 44)" filter={`url(#${id}-shadow)`}>
        <rect width="200" height="172" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="200" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <text x="14" y="24" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" fillOpacity="0.55">MONTHLY STATEMENT</text>
        <g transform="translate(14 34)" fontFamily="ui-monospace, Menlo, monospace">
          {[
            "Interchange",
            "Assessments",
            "Processing fees",
            "PCI / compliance",
            "Statement fee",
          ].map((label, i) => (
            <g key={i} transform={`translate(0 ${i * 20})`}>
              <text fontSize="9" fontWeight="700" fill="#061A3A">{label}</text>
              <rect x="120" y="-2" width="52" height="14" rx="3" fill="#EAF3FF" />
            </g>
          ))}
        </g>
      </g>
      <g transform="translate(288 78)" filter={`url(#${id}-shadow)`}>
        <rect width="132" height="70" rx="14" fill={`url(#${id}-navy)`} />
        <rect x="0" y="0" width="132" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <text x="14" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#FFFFFF" fillOpacity="0.65">Line by line</text>
        <text x="14" y="46" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="900" fill="#FFFFFF">Review your</text>
        <text x="14" y="60" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="900" fill="#FFFFFF">processing costs</text>
      </g>
    </Frame>
  );
}

/* ── 2) TYPICAL SETUP — terminal + card + receipt (neutral placeholder $) ── */
export function SetupIllustration() {
  const id = "ss";
  return (
    <Frame id={id} label="Payment terminal accepting a tap-to-pay card, with an itemized receipt">
      <rect x="40" y="42" width="34" height="34" rx="10" fill="#0072FF" fillOpacity="0.14" transform="rotate(-10 57 59)" />
      <circle cx="420" cy="70" r="20" fill="#3A8EFF" fillOpacity="0.20" />
      <g transform="translate(70 60)" filter={`url(#${id}-shadow)`}>
        <rect width="150" height="140" rx="16" fill={`url(#${id}-navy)`} />
        <rect x="10" y="10" width="130" height="80" rx="8" fill="#F6FAFF" />
        <rect x="10" y="10" width="130" height="14" rx="8" fill="#EAF3FF" />
        <text x="18" y="19" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="800" fill="#061A3A" fillOpacity="0.55">READY</text>
        <text x="75" y="52" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="18" fontWeight="900" fill="#061A3A">Amount</text>
        <rect x="18" y="60" width="114" height="18" rx="9" fill={`url(#${id}-accent)`} />
        <text x="75" y="72" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="800" fill="#FFFFFF">TAP · CHIP · SWIPE</text>
        <g transform="translate(20 100)">
          {[0,1,2,3,4,5,6,7,8].map(i => (
            <rect key={i} x={(i % 3) * 40} y={Math.floor(i / 3) * 12} width="30" height="8" rx="3" fill="#3A8EFF" fillOpacity={i === 4 ? 1 : 0.32} />
          ))}
        </g>
      </g>
      <g transform="translate(238 46) rotate(-10 66 42)" filter={`url(#${id}-shadow)`}>
        <rect width="132" height="82" rx="12" fill={`url(#${id}-accent)`} />
        <rect y="0" width="132" height="24" rx="12" fill={`url(#${id}-navy)`} />
        <rect y="14" width="132" height="10" fill="#061A3A" />
        <text x="10" y="17" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="800" fill="#FFFFFF">SWIPETRONICS</text>
        <rect x="10" y="34" width="24" height="16" rx="3" fill="#F6FAFF" />
        <g transform="translate(112 32)" fill="none" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round">
          <path d="M0 8 Q3 4 6 8" />
          <path d="M-3 12 Q3 0 9 12" />
        </g>
        <rect x="10" y="60" width="112" height="4" rx="2" fill="#FFFFFF" fillOpacity="0.55" />
      </g>
      <g transform="translate(276 128)" filter={`url(#${id}-shadow)`}>
        <rect width="128" height="96" rx="12" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="128" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <text x="12" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="800" fill="#061A3A">RECEIPT</text>
        <g transform="translate(12 32)">
          {[0, 1, 2].map(i => (
            <g key={i} transform={`translate(0 ${i * 12})`}>
              <rect width="52" height="4" rx="2" fill="#061A3A" fillOpacity="0.4" />
              <rect x="80" width="24" height="4" rx="2" fill="#061A3A" fillOpacity="0.55" />
            </g>
          ))}
        </g>
        <line x1="12" y1="72" x2="116" y2="72" stroke="#061A3A" strokeOpacity="0.15" strokeDasharray="2 2" />
        <rect x="12" y="80" width="104" height="12" rx="6" fill={`url(#${id}-accent)`} />
        <text x="64" y="89" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="800" fill="#FFFFFF">APPROVED</text>
      </g>
    </Frame>
  );
}

/* ── 3) SOLUTIONS — connected solutions cluster ────────────────────── */
export function SolutionsIllustration() {
  const id = "sv";
  const chips = [
    { x: 80,  y: 68,  t: "Card processing" },
    { x: 300, y: 60,  t: "POS systems" },
    { x: 68,  y: 190, t: "Terminals" },
    { x: 300, y: 198, t: "Cash Discount" },
  ];
  return (
    <Frame id={id} label="Central payment hub connected to Swipetronics solution categories">
      {chips.map((c, i) => (
        <line key={i} x1="230" y1="130" x2={c.x + 60} y2={c.y + 14}
              stroke="#0072FF" strokeOpacity="0.3" strokeWidth="1.4" strokeDasharray="4 4" />
      ))}
      {chips.map((c, i) => (
        <circle key={"m" + i} r="2.4" fill="#0072FF">
          <animateMotion dur={`${2.6 + i * 0.3}s`} repeatCount="indefinite" path={`M230 130 L${c.x + 60} ${c.y + 14}`} />
          <animate attributeName="fill-opacity" values="0;1;0" dur={`${2.6 + i * 0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}
      <g transform="translate(178 96)" filter={`url(#${id}-shadow)`}>
        <rect width="104" height="68" rx="14" fill={`url(#${id}-navy)`} />
        <rect x="0" y="0" width="104" height="24" rx="14" fill={`url(#${id}-accent)`} />
        <rect x="0" y="14" width="104" height="10" fill="#0072FF" />
        <text x="52" y="18" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="900" fill="#FFFFFF">SWIPETRONICS</text>
        <text x="52" y="46" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="900" fill="#FFFFFF">Your setup</text>
        <text x="52" y="60" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="700" fill="#FFFFFF" fillOpacity="0.65">One team · all pieces</text>
      </g>
      {chips.map((c, i) => (
        <g key={"c" + i} transform={`translate(${c.x} ${c.y})`} filter={`url(#${id}-shadow)`}>
          <rect width="120" height="28" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
          <rect x="0" y="0" width="4" height="28" rx="2" fill={`url(#${id}-accent)`} />
          <text x="60" y="18" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#061A3A">{c.t}</text>
        </g>
      ))}
    </Frame>
  );
}

/* ── 4) WHY IT MATTERS — neutral payment-volume chart, no % or $ claims ── */
export function WhyItMattersIllustration() {
  const id = "sw";
  return (
    <Frame id={id} label="Payment volume chart alongside neutral summary cards">
      <rect x="380" y="42" width="30" height="30" rx="8" fill="#0072FF" fillOpacity="0.14" transform="rotate(-12 395 57)" />
      <g transform="translate(50 46)" filter={`url(#${id}-shadow)`}>
        <rect width="240" height="172" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="240" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <text x="14" y="24" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" fillOpacity="0.55">PAYMENT VOLUME</text>
        <text x="14" y="50" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="900" fill="#061A3A">Understand your costs</text>
        <g transform="translate(14 74)">
          {[40, 52, 44, 66, 58, 72, 62, 78, 70, 84, 76, 88].map((h, i) => (
            <rect key={i} x={i * 18} y={80 - h * 0.6} width="12" height={h * 0.6} rx="2" fill="#3A8EFF" fillOpacity="0.55" />
          ))}
        </g>
        <path d="M14 108 L212 88" stroke={`url(#${id}-accent)`} strokeWidth="2.4" strokeLinecap="round" strokeDasharray="0" />
        <circle cx="212" cy="88" r="4" fill="#0072FF" />
      </g>
      <g transform="translate(292 60) rotate(6 66 40)" filter={`url(#${id}-shadow)`}>
        <rect width="132" height="80" rx="14" fill={`url(#${id}-navy)`} />
        <rect x="0" y="0" width="132" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <text x="14" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#FFFFFF" fillOpacity="0.65">Line by line</text>
        <text x="14" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="900" fill="#FFFFFF">Clearer pricing</text>
        <text x="14" y="58" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="800" fill="#3A8EFF">SIDE BY SIDE</text>
        <rect x="14" y="66" width="104" height="10" rx="5" fill={`url(#${id}-accent)`} />
      </g>
      <g transform="translate(300 156)" filter={`url(#${id}-shadow)`}>
        <rect width="120" height="42" rx="21" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <circle cx="21" cy="21" r="11" fill={`url(#${id}-accent)`} />
        <path d="M16 22 L20 26 L28 16" stroke="#FFFFFF" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x="40" y="18" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="700" fill="#061A3A" fillOpacity="0.55">Compare</text>
        <text x="40" y="30" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A">Your options</text>
      </g>
    </Frame>
  );
}

/* ── 5) FAQ — question cards + support chat ────────────────────────── */
export function FAQIllustration() {
  const id = "sf";
  return (
    <Frame id={id} label="Frequently asked question cards and a support conversation">
      <rect x="42" y="42" width="30" height="30" rx="8" fill="#0072FF" fillOpacity="0.14" transform="rotate(-12 57 57)" />
      <circle cx="410" cy="220" r="18" fill="#3A8EFF" fillOpacity="0.20" />
      <g transform="translate(52 60)" filter={`url(#${id}-shadow)`}>
        <rect width="220" height="60" rx="12" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="6" height="60" rx="3" fill={`url(#${id}-accent)`} />
        <circle cx="26" cy="22" r="8" fill={`url(#${id}-accent)`} fillOpacity="0.16" />
        <text x="26" y="26" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="900" fill="#0072FF">?</text>
        <text x="44" y="20" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#061A3A">Do you support tap-to-pay?</text>
        <text x="44" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="700" fill="#061A3A" fillOpacity="0.55">Yes — on every terminal we offer.</text>
        <rect x="44" y="42" width="96" height="10" rx="5" fill="#EAF3FF" />
      </g>
      <g transform="translate(78 132)" filter={`url(#${id}-shadow)`}>
        <rect width="220" height="60" rx="12" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="6" height="60" rx="3" fill={`url(#${id}-accent)`} />
        <circle cx="26" cy="22" r="8" fill={`url(#${id}-accent)`} fillOpacity="0.16" />
        <text x="26" y="26" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="900" fill="#0072FF">?</text>
        <text x="44" y="20" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#061A3A">Is Cash Discount a fit?</text>
        <text x="44" y="34" fontFamily="Inter, system-ui, sans-serif" fontSize="7.5" fontWeight="700" fill="#061A3A" fillOpacity="0.55">Depends on your business — we can walk through it.</text>
        <rect x="44" y="42" width="140" height="10" rx="5" fill="#EAF3FF" />
      </g>
      <g transform="translate(302 80)" filter={`url(#${id}-shadow)`}>
        <rect width="120" height="130" rx="14" fill={`url(#${id}-navy)`} />
        <rect x="0" y="0" width="120" height="26" rx="14" fill={`url(#${id}-accent)`} />
        <rect y="14" width="120" height="12" fill="#0072FF" />
        <text x="12" y="18" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="900" fill="#FFFFFF">SUPPORT</text>
        <circle cx="106" cy="14" r="4" fill="#3A8EFF" />
        <g transform="translate(10 34)">
          <rect width="72" height="14" rx="7" fill="#FFFFFF" fillOpacity="0.14" />
          <text x="10" y="10" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="700" fill="#FFFFFF">Hi — how can we help?</text>
        </g>
        <g transform="translate(38 54)">
          <rect width="72" height="14" rx="7" fill={`url(#${id}-accent)`} />
          <text x="10" y="10" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="800" fill="#FFFFFF">Setting up tips?</text>
        </g>
        <g transform="translate(10 74)">
          <rect width="72" height="14" rx="7" fill="#FFFFFF" fillOpacity="0.14" />
          <text x="10" y="10" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="700" fill="#FFFFFF">Yes — I'll walk you</text>
        </g>
        <g transform="translate(10 90)">
          <rect width="72" height="14" rx="7" fill="#FFFFFF" fillOpacity="0.14" />
          <text x="10" y="10" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="700" fill="#FFFFFF">through it now.</text>
        </g>
        <rect x="10" y="112" width="100" height="12" rx="6" fill="#FFFFFF" fillOpacity="0.06" />
        <circle cx="20" cy="118" r="2" fill="#3A8EFF">
          <animate attributeName="fill-opacity" values="0.3;1;0.3" dur="1.4s" repeatCount="indefinite" />
        </circle>
      </g>
    </Frame>
  );
}
